import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlvoLogo } from '../components/AlvoLogo';
import { ArrowLeft, Phone } from 'lucide-react';

const WHATSAPP_NUMBER = '5584999683171';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=Olá! Gostaria de falar sobre privacidade/termos do ALVO.`;
const CONTATO_EMAIL = 'contato@alvocfo.com';
const ULTIMA_ATUALIZACao = '04 de junho de 2026';

const LegalLayout = ({ title, subtitle, children, testid }) => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0E1A] text-white" data-testid={testid}>
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#0A0E1A]/90 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-3 group"
            data-testid="legal-home-link"
          >
            <AlvoLogo size={32} />
            <span className="font-bold text-lg tracking-tight">ALVO</span>
          </button>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
            data-testid="legal-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Voltar</span>
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight break-words">{title}</h1>
          {subtitle && <p className="mt-3 text-base text-zinc-400">{subtitle}</p>}
          <p className="mt-4 text-sm text-zinc-500">Última atualização: {ULTIMA_ATUALIZACao}</p>
        </div>

        <div className="space-y-8 text-zinc-300 leading-relaxed text-sm sm:text-base">
          {children}
        </div>

        {/* Contact footer */}
        <div className="mt-16 pt-8 border-t border-white/5">
          <p className="text-sm text-zinc-400">
            Dúvidas sobre este documento? Fale com a gente:
          </p>
          <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-3 text-sm">
            <a
              href={`mailto:${CONTATO_EMAIL}`}
              className="text-[#22C55E] hover:text-[#16a34a] transition-colors break-words"
              data-testid="legal-email-link"
            >
              {CONTATO_EMAIL}
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-[#25D366] hover:text-[#20bd5a] transition-colors"
              data-testid="legal-whatsapp-link"
            >
              <Phone className="w-4 h-4 shrink-0" />
              <span>(84) 99968-3171</span>
            </a>
          </div>
          <p className="mt-6 text-xs text-zinc-600">
            &copy; {new Date().getFullYear()} Alvo — Diretor Financeiro com I.A. Todos os direitos reservados.
          </p>
        </div>
      </main>
    </div>
  );
};

const Section = ({ n, title, children }) => (
  <section>
    <h2 className="text-lg md:text-xl font-semibold text-white mb-3">
      {n ? `${n}. ` : ''}{title}
    </h2>
    <div className="space-y-3">{children}</div>
  </section>
);

const Bullet = ({ children }) => (
  <li className="flex gap-2">
    <span className="text-[#22C55E] shrink-0 mt-1">•</span>
    <span className="min-w-0">{children}</span>
  </li>
);

/* ============================== PRIVACIDADE ============================== */
export const PrivacyPage = () => {
  return (
    <LegalLayout
      testid="privacy-page"
      title="Política de Privacidade"
      subtitle="Como o ALVO coleta, usa, armazena e protege seus dados, em conformidade com a LGPD (Lei nº 13.709/2018)."
    >
      <p>
        O ALVO (&quot;ALVO&quot;, &quot;nós&quot;, &quot;plataforma&quot;) é um software de gestão financeira
        e Diretor Financeiro com Inteligência Artificial, disponibilizado como serviço (SaaS) pela Alvo
        através do domínio <strong>alvocfo.com</strong>. Esta Política de Privacidade descreve como tratamos
        os dados pessoais e financeiros de nossos usuários e clientes (&quot;você&quot;).
      </p>

      <Section n="1" title="Dados que coletamos">
        <p>Para operar a plataforma, coletamos e tratamos os seguintes dados:</p>
        <ul className="space-y-2">
          <Bullet><strong>Dados cadastrais:</strong> nome, e-mail, telefone, nome da empresa e dados de acesso (login e senha criptografada).</Bullet>
          <Bullet><strong>Dados financeiros do seu negócio:</strong> receitas, despesas, lançamentos, metas, projeções, saldos bancários e arquivos financeiros importados por você (como extratos no formato OFX). Esses dados são fornecidos e controlados por você.</Bullet>
          <Bullet><strong>Dados de pagamento da assinatura:</strong> processados diretamente pela Stripe. Não armazenamos números de cartão de crédito em nossos servidores.</Bullet>
          <Bullet><strong>Dados de uso:</strong> informações técnicas de navegação, endereço IP, tipo de dispositivo e páginas acessadas, para segurança e melhoria do serviço.</Bullet>
        </ul>
      </Section>

      <Section n="2" title="Como usamos seus dados">
        <ul className="space-y-2">
          <Bullet>Fornecer, operar e manter as funcionalidades do ALVO (dashboards, DRE, fluxo de caixa, projeções, indicadores e análises com IA).</Bullet>
          <Bullet>Processar e gerenciar sua assinatura e pagamentos.</Bullet>
          <Bullet>Gerar diagnósticos, recomendações e análises financeiras automatizadas por meio de Inteligência Artificial.</Bullet>
          <Bullet>Enviar comunicações relacionadas ao serviço, suporte e avisos importantes.</Bullet>
          <Bullet>Garantir a segurança, prevenir fraudes e cumprir obrigações legais.</Bullet>
        </ul>
      </Section>

      <Section n="3" title="Inteligência Artificial">
        <p>
          O ALVO utiliza modelos de Inteligência Artificial para gerar análises e recomendações financeiras.
          Os dados enviados para processamento por IA são usados exclusivamente para produzir os resultados
          solicitados dentro da plataforma e <strong>não são utilizados para treinar modelos de terceiros</strong>.
          As recomendações geradas têm caráter informativo e não substituem o aconselhamento de um profissional
          contábil, financeiro ou jurídico.
        </p>
      </Section>

      <Section n="4" title="Compartilhamento de dados">
        <p>Não vendemos seus dados. Compartilhamos informações apenas com parceiros essenciais à operação:</p>
        <ul className="space-y-2">
          <Bullet><strong>Stripe:</strong> processamento de pagamentos.</Bullet>
          <Bullet><strong>Provedores de infraestrutura em nuvem e de IA:</strong> para hospedagem e processamento dos serviços.</Bullet>
          <Bullet><strong>Autoridades competentes:</strong> quando exigido por lei ou ordem judicial.</Bullet>
        </ul>
        <p>Todos os parceiros são obrigados a manter padrões adequados de segurança e confidencialidade.</p>
      </Section>

      <Section n="5" title="Armazenamento e segurança">
        <p>
          Seus dados são armazenados em servidores com medidas técnicas e organizacionais de proteção,
          incluindo criptografia de senhas, controle de acesso e conexões seguras (HTTPS). Mantemos seus
          dados enquanto sua conta estiver ativa e pelo período necessário ao cumprimento de obrigações legais.
        </p>
      </Section>

      <Section n="6" title="Seus direitos (LGPD)">
        <p>Você pode, a qualquer momento, solicitar:</p>
        <ul className="space-y-2">
          <Bullet>Confirmação da existência de tratamento e acesso aos seus dados.</Bullet>
          <Bullet>Correção de dados incompletos, inexatos ou desatualizados.</Bullet>
          <Bullet>Anonimização, bloqueio ou eliminação de dados desnecessários.</Bullet>
          <Bullet>Portabilidade e exportação dos seus dados.</Bullet>
          <Bullet>Eliminação dos dados tratados com seu consentimento e revogação do consentimento.</Bullet>
        </ul>
        <p>Para exercer seus direitos, entre em contato pelos canais indicados ao final desta página.</p>
      </Section>

      <Section n="7" title="Cookies">
        <p>
          Utilizamos cookies e tecnologias semelhantes para manter sua sessão, lembrar preferências e melhorar
          sua experiência. Ao acessar a plataforma, você pode aceitar ou recusar o uso de cookies não essenciais
          através do banner de consentimento. Cookies estritamente necessários ao funcionamento do serviço são
          sempre utilizados.
        </p>
      </Section>

      <Section n="8" title="Alterações nesta Política">
        <p>
          Podemos atualizar esta Política periodicamente. Alterações relevantes serão comunicadas pela plataforma
          ou por e-mail. A data da última atualização está indicada no topo deste documento.
        </p>
      </Section>
    </LegalLayout>
  );
};

/* ================================ TERMOS ================================ */
export const TermsPage = () => {
  return (
    <LegalLayout
      testid="terms-page"
      title="Termos de Uso"
      subtitle="Condições para utilização da plataforma ALVO. Ao usar o serviço, você concorda com estes termos."
    >
      <p>
        Estes Termos de Uso regulam o acesso e a utilização do ALVO, software de gestão financeira e Diretor
        Financeiro com Inteligência Artificial, oferecido como serviço (SaaS) pela Alvo no domínio{' '}
        <strong>alvocfo.com</strong>. Ao criar uma conta ou utilizar a plataforma, você declara que leu, entendeu
        e concorda com estes termos.
      </p>

      <Section n="1" title="Objeto do serviço">
        <p>
          O ALVO disponibiliza ferramentas para organização financeira, importação e classificação de lançamentos,
          elaboração de DRE, fluxo de caixa, projeções, indicadores de desempenho e análises/recomendações geradas
          por Inteligência Artificial. O serviço é uma ferramenta de apoio à gestão e <strong>não constitui
          aconselhamento contábil, financeiro, de investimento ou jurídico</strong>.
        </p>
      </Section>

      <Section n="2" title="Cadastro e conta">
        <ul className="space-y-2">
          <Bullet>Você deve fornecer informações verdadeiras, completas e atualizadas no cadastro.</Bullet>
          <Bullet>Você é responsável pela confidencialidade das suas credenciais e por toda atividade realizada na sua conta.</Bullet>
          <Bullet>É necessário ser maior de 18 anos ou representar validamente a empresa usuária.</Bullet>
        </ul>
      </Section>

      <Section n="3" title="Assinatura, pagamentos e cancelamento">
        <ul className="space-y-2">
          <Bullet>O acesso às funcionalidades pagas depende de assinatura ativa, conforme o plano contratado.</Bullet>
          <Bullet>Os pagamentos são processados pela <strong>Stripe</strong>. A renovação pode ocorrer automaticamente conforme a modalidade escolhida.</Bullet>
          <Bullet>Você pode cancelar a assinatura a qualquer momento; o acesso permanece ativo até o fim do período já pago.</Bullet>
          <Bullet>Valores já pagos não são reembolsados proporcionalmente, salvo quando exigido por lei (como o direito de arrependimento em até 7 dias para contratações a distância).</Bullet>
        </ul>
      </Section>

      <Section n="4" title="Responsabilidades do usuário">
        <ul className="space-y-2">
          <Bullet>Você é o único responsável pela veracidade e exatidão dos dados financeiros inseridos ou importados.</Bullet>
          <Bullet>Você não deve usar a plataforma para fins ilícitos, nem tentar burlar, sobrecarregar ou comprometer sua segurança.</Bullet>
          <Bullet>As decisões tomadas com base nas informações e análises do ALVO são de sua inteira responsabilidade.</Bullet>
        </ul>
      </Section>

      <Section n="5" title="Propriedade intelectual">
        <p>
          Todo o software, marca, design, textos e funcionalidades do ALVO são de propriedade da Alvo e protegidos
          por lei. Seus dados financeiros permanecem seus; concedemos apenas uma licença de uso da plataforma,
          sem transferir qualquer direito sobre o software.
        </p>
      </Section>

      <Section n="6" title="Limitação de responsabilidade">
        <p>
          O ALVO é fornecido &quot;como está&quot;. Embora nos empenhemos em oferecer um serviço preciso e disponível,
          não garantimos ausência total de erros ou interrupções. Na máxima extensão permitida por lei, a Alvo não se
          responsabiliza por perdas ou prejuízos decorrentes de decisões tomadas com base nas análises, nem por
          indisponibilidades causadas por terceiros.
        </p>
      </Section>

      <Section n="7" title="Suspensão e encerramento">
        <p>
          Podemos suspender ou encerrar contas que violem estes Termos, a legislação aplicável ou que apresentem
          risco à segurança da plataforma. Você também pode encerrar sua conta a qualquer momento.
        </p>
      </Section>

      <Section n="8" title="Privacidade">
        <p>
          O tratamento dos seus dados é regido pela nossa Política de Privacidade, que integra estes Termos de Uso.
        </p>
      </Section>

      <Section n="9" title="Alterações e foro">
        <p>
          Estes Termos podem ser atualizados periodicamente, com aviso pela plataforma. O uso continuado após as
          alterações implica concordância. Estes Termos são regidos pelas leis brasileiras, elegendo-se o foro do
          domicílio do usuário consumidor para dirimir eventuais controvérsias.
        </p>
      </Section>
    </LegalLayout>
  );
};
